import { motion } from "framer-motion";

const reasons = [
  {
    icon: "🤝",
    title: "Employee-First Culture",
    desc: "Work culture focused on staff welfare and teamwork, ensuring every meal is prepared with care and dedication.",
  },
  {
    icon: "⏱️",
    title: "Timely Delivery, Always",
    desc: "On-time food delivery regardless of operational challenges or circumstances — no excuses, no delays.",
  },
  {
    icon: "🥬",
    title: "Fresh & Quality Ingredients",
    desc: "Every meal prepared using fresh, high-quality, and carefully sourced ingredients for authentic taste.",
  },
  {
    icon: "🕐",
    title: "24/7 Food Service",
    desc: "Round-the-clock operations covering breakfast, lunch, snacks, dinner, and supper — every single day.",
  },
  {
    icon: "✨",
    title: "Strict Hygiene & Consistency",
    desc: "Uncompromising hygiene standards and consistent taste across every meal served, every time.",
  },
  {
    icon: "🍛",
    title: "Authentic South Indian Cuisine",
    desc: "Commitment to authentic South Indian vegetarian cuisine with homestyle quality your team will love.",
  },
];

export function WhyChooseUs() {
  return (
    <section id="why-us" className="relative py-20 sm:py-28 md:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.65 }}
          className="mb-12 sm:mb-16 text-center"
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-12 bg-gold" />
            <span className="text-xs uppercase tracking-[0.4em] text-gold">Why Choose Us</span>
            <span className="h-px w-12 bg-gold" />
          </div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-5xl leading-tight">
            The Mononites{" "}
            <span className="gradient-gold-text italic">difference.</span>
          </h2>
          <p className="mt-4 text-muted-foreground text-sm sm:text-base font-light max-w-2xl mx-auto">
            We don't just serve food — we build trust through reliability, quality, and an
            unwavering commitment to your team's well-being.
          </p>
        </motion.div>

        <div className="group/grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
          {reasons.map((r, i) => (
            <motion.div
              key={r.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              whileHover={{ y: -8, scale: 1.03, transition: { duration: 0.3, ease: "easeOut" } }}
              className="glass-card rounded-xl sm:rounded-2xl p-6 sm:p-7 group/card hover:border-[var(--gold)]/40 hover:shadow-[0_20px_60px_-12px_oklch(0.78_0.15_78/20%)] transition-all duration-300 group-hover/grid:opacity-60 hover:!opacity-100"
            >
              <div className="text-3xl mb-3 transition-transform duration-300 group-hover/card:scale-110">{r.icon}</div>
              <h3 className="text-lg sm:text-xl font-light text-gold mb-2">{r.title}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed font-light">
                {r.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* 365 days highlight bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-10 sm:mt-14 glass-card rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-center border-[var(--gold)]/20"
        >
          <span className="text-4xl sm:text-5xl text-muted-foreground text-gold">365</span>
          <div className="text-sm sm:text-base text-muted-foreground font-light">
            days a year of <span className="text-foreground font-medium">uninterrupted service</span> — ensuring our clients never experience a pause in food operations.
          </div>
        </motion.div>
      </div>
    </section>
  );
}
